import type {
  MessageFormatAdapter,
  ThreadHistoryAdapter,
  GenericThreadHistoryAdapter,
} from '@assistant-ui/react';
import {
  loadServerThreadBranch,
  loadServerThreadMessages,
  mirrorMessage,
  seedServerThread,
} from './server-threads';

/**
 * The assistant-ui history boundary for a company thread.
 *
 * Reads hydrate from the authenticated thread endpoint into the process-local
 * cache, and every write mirrors to that same server-owned thread. Nothing is
 * persisted in browser storage and signed-out mounts are inert.
 */
export function serverThreadHistory(
  threadId: string,
  {
    enabled = true,
    appendable = true,
    isCurrentOwner = () => true,
    whenSessionKnown,
  }: {
    enabled?: boolean;
    /** False for a reader of someone else's shared chat (collaboration v1):
     * the chat loads, and nothing is ever written back to it. */
    appendable?: boolean;
    isCurrentOwner?: () => boolean;
    /** Resolves with whether the session is signed in once /me has answered. A pane that mounts first (a deep link
     * right after login) waits for it instead of loading an empty history the SDK never retries. */
    whenSessionKnown?: Promise<boolean>;
  } = {},
): ThreadHistoryAdapter {
  const load = async <TMessage, TStorageFormat extends Record<string, unknown>>(
    format: MessageFormatAdapter<TMessage, TStorageFormat>,
  ): Promise<Awaited<ReturnType<GenericThreadHistoryAdapter<TMessage>['load']>>> => {
    let signedIn = enabled;
    if (!signedIn && whenSessionKnown) {
      signedIn = await whenSessionKnown;
      // The owner guard commits with the same session event; give it a moment before treating it as foreign.
      for (let wait = 0; signedIn && !isCurrentOwner() && wait < 50; wait += 1)
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (!signedIn || !isCurrentOwner()) return { headId: null, messages: [] };
    await seedServerThread(threadId, isCurrentOwner);
    const rows = loadServerThreadMessages(threadId);
    const branch = loadServerThreadBranch(threadId);
    const messages = rows.map((message, index) => ({
      parentId: branch
        ? (branch.parents.get(message.id) ?? null)
        : index > 0
          ? rows[index - 1].id
          : null,
      message: {
        id: message.id,
        role: message.role,
        parts: message.parts,
        ...(message.metadata === undefined ? {} : { metadata: message.metadata }),
      } as TMessage,
    }));
    // The server wire format is already the assistant-ui message shape. Run it
    // through the supplied adapter so this boundary remains format-correct if
    // the runtime changes its storage representation.
    return {
      headId:
        branch?.headId ??
        (messages.at(-1)?.message ? format.getId(messages.at(-1)!.message) : null),
      messages,
    };
  };

  const append = async <TMessage>(
    item: { parentId: string | null; message: TMessage },
    format: MessageFormatAdapter<TMessage, Record<string, unknown>>,
  ) => {
    if (!enabled || !appendable || !isCurrentOwner()) return;
    const message = item.message as TMessage & {
      id: string;
      role: string;
      parts?: unknown[];
    };
    await mirrorMessage(
      threadId,
      {
        id: format.getId(item.message),
        role: message.role,
        parts: message.parts ?? [],
      },
      { isCurrentOwner },
    );
  };

  return {
    async load() {
      return { headId: null, messages: [] };
    },
    async append() {},
    withFormat<TMessage, TStorageFormat extends Record<string, unknown>>(
      format: MessageFormatAdapter<TMessage, TStorageFormat>,
    ): GenericThreadHistoryAdapter<TMessage> {
      return {
        load: () => load(format),
        append: (item) =>
          append(item, format as MessageFormatAdapter<TMessage, Record<string, unknown>>),
      };
    },
  };
}
